/**
 * 消息中心：会话列表（团队群聊 + 1v1）+ 对话区 + 好友管理。
 *
 * - 左栏：会话列表（未读角标、最后一条预览），顶部 Tab「会话 / 好友」；
 * - 右栏：选中会话的 ChatRoom（3s 轮询）；无会话时引导去发起；
 * - 好友面板：待我处理的申请 / 我发出的申请 / 好友列表（发消息、删除、拉黑）。
 * 1v1 临时对话从「候选人/成员卡」上的「发消息」进入（openDirect），不在此页发起。
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chatApi } from '../api/chat.js';
import type { ConversationDto, FriendDto } from '../api/types.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { ChatRoom } from '../components/ChatRoom.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';
import { formatRelative, schoolFullLabel } from '../lib/format.js';
import { qk } from '../lib/queryKeys.js';
import { toast } from '../store/toast.js';

function FriendRow({
  friend,
  actions,
}: {
  friend: FriendDto;
  actions?: ReactNode;
}) {
  return (
    <div className="friend-row">
      <div>
        <strong>{friend.name}</strong>{' '}
        <span className="muted small">{schoolFullLabel(friend.school)}</span>
        {friend.headline !== null && friend.headline.length > 0 ? (
          <div className="muted small">{friend.headline}</div>
        ) : null}
      </div>
      {actions !== undefined ? <div className="row-tight">{actions}</div> : null}
    </div>
  );
}

function FriendsPanel({ onOpenChat }: { onOpenChat: (userId: string) => void }) {
  const queryClient = useQueryClient();
  const friendsQuery = useQuery({ queryKey: qk.friends(), queryFn: () => chatApi.listFriends() });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: qk.friends() });
    void queryClient.invalidateQueries({ queryKey: qk.conversations() });
  };

  const accept = useMutation({
    mutationFn: (userId: string) => chatApi.acceptFriend(userId),
    onSuccess: () => {
      invalidate();
      toast.success('已加为好友', '现在可以互相私聊了');
    },
    onError: (error) => toast.error('操作失败', toUserMessage(error)),
  });
  const decline = useMutation({
    mutationFn: (userId: string) => chatApi.declineFriend(userId),
    onSuccess: () => {
      invalidate();
      toast.success('已拒绝申请');
    },
    onError: (error) => toast.error('操作失败', toUserMessage(error)),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => chatApi.removeFriend(userId),
    onSuccess: () => {
      invalidate();
      toast.success('已删除好友', '如需恢复可以重新添加');
    },
    onError: (error) => toast.error('操作失败', toUserMessage(error)),
  });
  const block = useMutation({
    mutationFn: (userId: string) => chatApi.blockUser(userId),
    onSuccess: () => {
      invalidate();
      toast.success('已拉黑', '对方不能再向你发起私聊');
    },
    onError: (error) => toast.error('操作失败', toUserMessage(error)),
  });

  if (friendsQuery.isPending) return <Loading label="加载好友中" />;
  if (friendsQuery.isError) {
    return <ErrorState error={friendsQuery.error} title="读取好友失败" onRetry={() => void friendsQuery.refetch()} />;
  }
  const data = friendsQuery.data;

  return (
    <div className="friends-panel">
      <section>
        <h3 className="card-sub">待我处理的申请（{data.incoming.length}）</h3>
        {data.incoming.length === 0 ? <p className="muted small">暂无待处理申请</p> : null}
        {data.incoming.map((friend) => (
          <FriendRow
            key={friend.userId}
            friend={friend}
            actions={
              <>
                <Button size="sm" loading={accept.isPending && accept.variables === friend.userId} onClick={() => accept.mutate(friend.userId)}>
                  通过
                </Button>
                <Button size="sm" variant="ghost" loading={decline.isPending && decline.variables === friend.userId} onClick={() => decline.mutate(friend.userId)}>
                  拒绝
                </Button>
              </>
            }
          />
        ))}
      </section>

      <section>
        <h3 className="card-sub">我发出的申请（{data.pending.length}）</h3>
        {data.pending.length === 0 ? <p className="muted small">暂无发出的申请</p> : null}
        {data.pending.map((friend) => (
          <FriendRow
            key={friend.userId}
            friend={friend}
            actions={
              <Button size="sm" variant="ghost" loading={remove.isPending && remove.variables === friend.userId} onClick={() => remove.mutate(friend.userId)}>
                取消申请
              </Button>
            }
          />
        ))}
      </section>

      <section>
        <h3 className="card-sub">我的好友（{data.friends.length}）</h3>
        {data.friends.length === 0 ? (
          <p className="muted small">还没有好友——在候选人或队伍成员卡片上点「加好友」</p>
        ) : null}
        {data.friends.map((friend) => (
          <FriendRow
            key={friend.userId}
            friend={friend}
            actions={
              <>
                <Button size="sm" onClick={() => onOpenChat(friend.userId)}>
                  发消息
                </Button>
                <Button size="sm" variant="ghost" loading={remove.isPending && remove.variables === friend.userId} onClick={() => remove.mutate(friend.userId)}>
                  删除
                </Button>
                <Button size="sm" variant="ghost" loading={block.isPending && block.variables === friend.userId} onClick={() => block.mutate(friend.userId)}>
                  拉黑
                </Button>
              </>
            }
          />
        ))}
      </section>
    </div>
  );
}

export function MessagesPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get('c'));
  const [leftTab, setLeftTab] = useState<'conversations' | 'friends'>('conversations');

  // 选中会话同步到 URL ?c=，支持从成员卡/好友列表直达
  const selectConversation = (id: string): void => {
    setSelectedId(id);
    setSearchParams({ c: id }, { replace: true });
  };

  const conversationsQuery = useQuery({
    queryKey: qk.conversations(),
    queryFn: () => chatApi.listConversations(),
    refetchInterval: 10_000,
  });

  const conversations = conversationsQuery.data?.conversations ?? [];
  const active: ConversationDto | null =
    conversations.find((item) => item.id === selectedId) ?? conversations[0] ?? null;

  // 会话列表刷新后，若选中的会话消失（被拉黑等）自动回落到第一个
  useEffect(() => {
    if (active === null && selectedId !== null) {
      setSelectedId(null);
      setSearchParams({}, { replace: true });
    }
  }, [active, selectedId, setSearchParams]);

  const openDirect = useMutation({
    mutationFn: (userId: string) => chatApi.openDirect(userId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.conversations() });
      selectConversation(data.conversation.id);
      setLeftTab('conversations');
    },
    onError: (error) => toast.error('发起私聊失败', toUserMessage(error)),
  });

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">消息</h1>
          <p className="page-sub">队伍群聊与私聊都在这里；3 秒自动刷新，无需手动刷新页面。</p>
        </div>
      </div>

      <div className="messages-layout">
        <aside className="messages-list">
          <div className="tab-scroll" role="tablist" aria-label="消息侧栏切换">
            <button
              type="button"
              role="tab"
              aria-selected={leftTab === 'conversations'}
              className={leftTab === 'conversations' ? 'chip on' : 'chip'}
              onClick={() => setLeftTab('conversations')}
            >
              会话
              {(conversationsQuery.data?.totalUnread ?? 0) > 0 ? (
                <Tag tone="primary">{conversationsQuery.data?.totalUnread}</Tag>
              ) : null}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={leftTab === 'friends'}
              className={leftTab === 'friends' ? 'chip on' : 'chip'}
              onClick={() => setLeftTab('friends')}
            >
              好友
            </button>
          </div>

          {leftTab === 'conversations' ? (
            conversationsQuery.isPending ? (
              <Loading label="加载会话中" />
            ) : conversationsQuery.isError ? (
              <ErrorState error={conversationsQuery.error} title="读取会话失败" onRetry={() => void conversationsQuery.refetch()} />
            ) : conversations.length === 0 ? (
              <EmptyState title="暂无会话" description="确认成队后队伍群聊自动开启；也可以在成员卡片上发起临时对话" />
            ) : (
              <ul className="conversation-list">
                {conversations.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={item.id === active?.id ? 'conversation-item on' : 'conversation-item'}
                      onClick={() => selectConversation(item.id)}
                    >
                      <span className="conversation-title">
                        <Tag tone={item.type === 'team' ? 'primary' : 'muted'}>
                          {item.type === 'team' ? '群聊' : '私聊'}
                        </Tag>
                        {item.title}
                        {item.unread > 0 ? <span className="unread-badge">{item.unread}</span> : null}
                      </span>
                      <span className="muted small conversation-preview">
                        {item.lastMessage !== null ? `${item.lastMessage.senderName}：${item.lastMessage.body}` : '还没有消息'}
                      </span>
                      <span className="muted small">
                        {item.lastMessage !== null ? formatRelative(item.lastMessage.createdAt) : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <FriendsPanel onOpenChat={(userId) => openDirect.mutate(userId)} />
          )}
        </aside>

        <section className="messages-main">
          {active === null ? (
            <Card title="选择一个会话">
              <p className="muted small">从左侧选中会话开始聊天；或到「好友」里发起私聊。</p>
            </Card>
          ) : (
            <ChatRoom
              conversationId={active.id}
              title={active.title}
              subtitle={
                active.type === 'direct' && active.peer !== null
                  ? `${schoolFullLabel(active.peer.school)} · 临时对话`
                  : active.type === 'team'
                    ? '队伍群聊'
                    : undefined
              }
            />
          )}
        </section>
      </div>
    </div>
  );
}
